import {
  CheckCircle2,
  Clock,
  Loader2,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import { type ContentCompletionRequestResult } from '@/entities/user';
import { cn } from '@/shared/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/shared/ui/empty';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';

const formatDate = (input?: string | null) => {
  if (!input) {
    return '-';
  }

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const statusStyles: Record<
  ContentCompletionRequestResult['status'],
  {
    badgeClass: string;
    cardClass: string;
    iconClass: string;
    icon: LucideIcon;
    label: string;
  }
> = {
  PENDING: {
    badgeClass: 'border border-amber-200 bg-amber-50 text-amber-700',
    cardClass: 'border-amber-200/70 bg-amber-50/30',
    iconClass: 'text-amber-500',
    icon: Clock,
    label: 'Pending review',
  },
  APPROVED: {
    badgeClass: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
    cardClass: 'border-emerald-200/70 bg-emerald-50/25',
    iconClass: 'text-emerald-500',
    icon: CheckCircle2,
    label: 'Approved',
  },
  REJECTED: {
    badgeClass: 'border border-rose-200 bg-rose-50 text-rose-700',
    cardClass: 'border-rose-200/70 bg-rose-50/30',
    iconClass: 'text-rose-500',
    icon: XCircle,
    label: 'Rejected',
  },
};

interface RequestCardProps {
  request: ContentCompletionRequestResult;
}

const RequestCard = ({ request }: RequestCardProps) => {
  const statusStyle = statusStyles[request.status];
  const item = request.form_data?.items?.[0];
  const hasNotes = Boolean(request.admin_notes?.trim());
  const StatusIcon = statusStyle.icon;

  return (
    <Card
      className={cn(
        'border border-border/60 bg-background p-4 shadow-sm transition-shadow hover:shadow-md',
        statusStyle.cardClass,
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex-1 space-y-1">
            <h3 className="text-sm font-semibold leading-tight text-foreground">
              {item?.content?.title ?? 'Untitled Content'}
            </h3>
            <p className="text-xs text-muted-foreground">
              {item?.course?.title ?? 'Course'}
            </p>
          </div>
          <Badge
            className={cn(
              'inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em]',
              statusStyle.badgeClass,
            )}
          >
            <StatusIcon className={cn('h-3.5 w-3.5', statusStyle.iconClass)} />
            {statusStyle.label}
          </Badge>
        </div>
        <dl className="grid gap-3 text-sm text-muted-foreground grid-cols-2">
          <div className="space-y-1">
            <dt className="text-xs font-semibold uppercase tracking-[0.14em]">
              Completion date
            </dt>
            <dd className="text-sm font-medium text-foreground">
              {formatDate(item?.claimed_completion_datetime)}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-xs font-semibold uppercase tracking-[0.14em]">
              Submitted
            </dt>
            <dd className="text-sm font-medium text-foreground">
              {formatDate(request.created_at)}
            </dd>
          </div>
        </dl>
        {hasNotes ? (
          <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 p-4 text-sm text-foreground">
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Admin notes
            </p>
            <p>{request.admin_notes}</p>
          </div>
        ) : null}
      </div>
    </Card>
  );
};

const RequestSkeleton = () => (
  <Card className="border border-dashed border-border/60 bg-muted/10 p-4 shadow-none">
    <div className="space-y-3">
      <Skeleton className="h-4 w-2/3" />
      <div className="grid gap-2 grid-cols-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
      </div>
      <Skeleton className="h-9 w-full" />
    </div>
  </Card>
);

export interface CompletionRequestsProps {
  completionRequests: ContentCompletionRequestResult[];
  isRequestsLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

export const CompletionRequests = ({
  completionRequests,
  isRequestsLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: CompletionRequestsProps) => (
  <Card className="border border-border/60 bg-background shadow-sm">
    <CardHeader className="gap-3 p-5 pb-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle className="text-base font-semibold">
            Submitted Requests
          </CardTitle>
          <CardDescription className="text-sm leading-relaxed text-muted-foreground">
            Track each manual completion submission and reviewer feedback.
          </CardDescription>
        </div>
        {!isRequestsLoading && completionRequests.length > 0 ? (
          <Badge className="rounded-full border border-border/60 bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {completionRequests.length}{' '}
            {completionRequests.length === 1 ? 'request' : 'requests'}
          </Badge>
        ) : null}
      </div>
    </CardHeader>
    <CardContent className="space-y-4 p-5 pt-0">
      {isRequestsLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, index) => (
            <RequestSkeleton key={index} />
          ))}
        </div>
      ) : !completionRequests.length ? (
        <div className="rounded-2xl border border-dashed border-border/60 bg-muted/10 p-10 text-center">
          <Empty className="mx-auto max-w-sm border-none p-0 text-center">
            <EmptyHeader className="gap-4">
              <EmptyMedia variant="icon" className="mx-auto">
                <Clock className="h-9 w-9 text-muted-foreground" />
              </EmptyMedia>
              <EmptyTitle className="text-base font-semibold">
                No requests submitted
              </EmptyTitle>
              <EmptyDescription>
                Submit a request to check its review status and notes from our
                team.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      ) : (
        <div className="space-y-4">
          {completionRequests.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </div>
      )}
    </CardContent>
    {hasNextPage ? (
      <CardFooter className="justify-center border-t border-border/60 bg-muted/20 p-5">
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 rounded-full border-dashed px-5 text-sm font-semibold"
          onClick={onLoadMore}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Clock className="h-4 w-4" />
          )}
          Load more
        </Button>
      </CardFooter>
    ) : null}
  </Card>
);
